import { useMemo } from "react"
import { ButtonProps } from "@chakra-ui/react"
import { APPROVE_AND_CALL_TOKENS } from "@mezo/sdk"

import {
  RootState,
  selectActiveToken,
  selectCanUsePortal,
  selectDepositAmount,
  selectDepositFromAnotherWallet,
  selectDepositStatus,
  selectTokenApprovalStatus,
  selectTokenBalance,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import { BITCOIN_MIN_DEPOSIT } from "#/shared/constants"
import { floatToBigInt } from "#/shared/utils"

export const useDepositButtonStylesForDepositStatus = (
  defaultButtonProps?: ButtonProps,
  loaderBurronProps?: ButtonProps,
) => {
  const depositStatus = useDappSelector(selectDepositStatus)

  return depositStatus === "pending"
    ? { ...defaultButtonProps, ...loaderBurronProps }
    : defaultButtonProps
}

const useDepositButton = () => {
  const depositAmount = useDappSelector(selectDepositAmount)
  const activeToken = useDappSelector(selectActiveToken)
  const tokenApprovalStatus = useDappSelector(selectTokenApprovalStatus)

  const tokenBalance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, activeToken),
  )

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, activeToken),
  )

  const amount = useMemo(
    () => floatToBigInt(depositAmount, tokenDecimals),
    [depositAmount, tokenDecimals],
  )

  const incorrectDepositInput = useMemo(
    () => amount > tokenBalance || amount <= 0n,
    [amount, tokenBalance],
  )

  const canUsePortal = useDappSelector(selectCanUsePortal)

  const missingApprove =
    APPROVE_AND_CALL_TOKENS.includes(activeToken) || activeToken === "BTC"
      ? false
      : tokenApprovalStatus === "non-approved"

  const isDisabled = useMemo(
    () => !canUsePortal || missingApprove || incorrectDepositInput,
    [canUsePortal, missingApprove, incorrectDepositInput],
  )

  return { isDisabled }
}

export const useBlockDepositButton = () => {
  const depositStatus = useDappSelector(selectDepositStatus)
  const { isDisabled } = useDepositButton()

  return isDisabled || depositStatus === "pending"
}

export const useContinueButton = () => {
  const shouldBlockButton = useBlockDepositButton()

  const depositFromAnotherWallet = useDappSelector(
    selectDepositFromAnotherWallet,
  )

  const depositAmount = useDappSelector(selectDepositAmount)

  const bitcoinBalance = useDappSelector((state: RootState) =>
    selectTokenBalance(state, "BTC"),
  )

  const bitcoinDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, "BTC"),
  )

  const bitcoinDepositAboveThreshold = useMemo(
    () => Number(depositAmount) >= BITCOIN_MIN_DEPOSIT,
    [depositAmount],
  )

  const validBitcoinDeposit = useMemo(
    () =>
      floatToBigInt(depositAmount, bitcoinDecimals) <= bitcoinBalance &&
      bitcoinDepositAboveThreshold,
    [
      bitcoinBalance,
      depositAmount,
      bitcoinDecimals,
      bitcoinDepositAboveThreshold,
    ],
  )

  const isDisabled = useMemo(
    () =>
      depositFromAnotherWallet
        ? !bitcoinDepositAboveThreshold
        : shouldBlockButton || !validBitcoinDeposit,
    [
      depositFromAnotherWallet,
      bitcoinDepositAboveThreshold,
      validBitcoinDeposit,
      shouldBlockButton,
    ],
  )

  return { isDisabled }
}
